import React, { useEffect, useState } from 'react';
import { DefaultButton, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { operatorKey } from '../utils/worklistOperators';
import { confirmMaluchEmpatia, getMaluchOperatorFacilities } from '../api/prospectingApi';

/** An on-demand shortlist; it does not prefetch operators for the map or the review queue. */
export default function MaluchOperatorFacilities({ token, application, onOpenEntity, onChanged }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [linked, setLinked] = useState('');
  const id = application?.id;

  useEffect(() => {
    setOpen(false); setResponse(null); setError(''); setLinked('');
  }, [id]);

  useEffect(() => {
    if (!open || !token || !id || response) return undefined;
    const abort = new AbortController();
    setBusy(true); setError('');
    getMaluchOperatorFacilities(token, id, abort.signal)
      .then((data) => { if (!abort.signal.aborted) setResponse(data); })
      .catch((e) => { if (!abort.signal.aborted && e.name !== 'AbortError') setError(e.message || 'Nie udało się pobrać placówek.'); })
      .finally(() => { if (!abort.signal.aborted) setBusy(false); });
    return () => abort.abort();
  }, [open, id, response, token]);

  const current = String(linked || application?.matched_entity_id || '');
  const status = linked ? 'confirmed' : String(application?.match_status || '');
  const isLinked = status === 'confirmed' || status === 'linked_probable';

  async function confirm(item) {
    if (isLinked && current && current !== item.id) return;
    if (!window.confirm(`Powiązać wniosek ${application.application_no} z placówką Empatia „${item.name}”? Sprawdź, czy to ten sam obiekt. Dane placówki Empatia nie zostaną nadpisane.`)) return;
    setBusy(true); setError('');
    try {
      await confirmMaluchEmpatia(token, id, item.id);
      setLinked(item.id);
      onChanged?.();
    } catch (e) { setError(e.message || 'Nie udało się potwierdzić.'); }
    finally { setBusy(false); }
  }

  function openWorklist() {
    const exactOwner = response?.same_operator?.[0];
    const key = exactOwner ? operatorKey(exactOwner) : '';
    const filters = new URLSearchParams({ new: 'all', customers: 'include' });
    if (key) filters.set('operator', key);
    else if (response?.applicant) filters.set('q', response.applicant);
    navigate(`/prospecting/lista?${filters.toString()}`);
  }

  function facility(item) {
    const canConfirm = !isLinked || current === item.id;
    const activeConfirmed = current === item.id && status === 'confirmed';
    return (
      <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #e4e4e4' }}>
        <strong>{item.name}</strong>{item.is_current_match || current === item.id ? ' · obecne powiązanie' : ''}
        <div>{item.institution_type || 'Żłobek / klub'} · {item.commune || 'gmina niepodana'} · miejsc: {item.capacity ?? 'brak'}</div>
        <div>Empatia: {item.registry_id} · {item.address || 'brak adresu'} · prowadzący: {item.operator_name || 'niepodany'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {onOpenEntity ? <DefaultButton text="Otwórz kartę" onClick={() => onOpenEntity(item.id)} /> : null}
          {canConfirm && !activeConfirmed ? <DefaultButton text={current === item.id ? 'Potwierdź obecne powiązanie' : 'To ta placówka — powiąż'} disabled={busy} onClick={() => confirm(item)} /> : null}
          {activeConfirmed ? <span style={{ paddingTop: 5 }}>Potwierdzone ręcznie</span> : null}
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: '10px 0' }}>
      <DefaultButton text={open ? 'Ukryj placówki prowadzącego' : 'Znajdź placówki tego prowadzącego'} onClick={() => setOpen((value) => !value)} />
      {open ? (
        <div style={{ marginTop: 10, padding: 12, background: '#f8fafc', border: '1px solid #e4e8ec' }}>
          {busy ? <Spinner label="Sprawdzam placówki Empatii…" /> : null}
          {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
          {response ? <>
            <div className="prospecting-maluch-operator-actions">
              <strong>Wnioskodawca / organ: {response.applicant || 'brak danych'}</strong>
              {response.applicant || response.same_operator?.length ? <DefaultButton text="Wyszukaj tego prowadzącego na Liście handlowca" onClick={openWorklist} /> : null}
            </div>
            {isLinked ? <div style={{ marginTop: 5 }}><strong>Obecna Empatia:</strong> {current} · {status === 'linked_probable' ? 'dopasowanie automatyczne, niepotwierdzone' : 'potwierdzone'}</div> : null}
            <h4>Placówki prowadzącego — nazwa / NIP z powiązania ({(response.same_operator || []).length})</h4>
            <p style={{ fontSize: 12 }}>Wyniki wg NIP mogą pochodzić z automatycznego powiązania MALUCH+ z Empatią. Zweryfikuj operatora przed potwierdzeniem tożsamości.</p>
            {(response.same_operator || []).length ? response.same_operator.map(facility) : <p>Brak dokładnego dopasowania pełnej nazwy operatora w Empatii.</p>}
            <h4>Inne placówki opieki w tej gminie — do sprawdzenia ({(response.same_commune || []).length})</h4>
            <p style={{ fontSize: 12 }}>Sama gmina nie potwierdza tego samego prowadzącego. Sprawdź nazwę, adres i liczbę miejsc przed powiązaniem.</p>
            {(response.same_commune || []).length ? response.same_commune.map(facility) : <p>Brak dodatkowych żłobków lub klubów dla tej formy opieki.</p>}
            {isLinked && (response.same_operator || []).concat(response.same_commune || []).some((e) => e.id !== current) ?
              <p style={{ fontSize: 12 }}>Zmiana już zapisanego powiązania na inną placówkę wymaga osobnej korekty relacji, aby nie pozostawić błędnych identyfikatorów i dowodów kontaktowych.</p> : null}
          </> : null}
        </div>
      ) : null}
    </div>
  );
}
